import { useEffect } from 'react';

/**
 * Suppresses password-manager in-field autofill icons (LastPass, 1Password, etc.)
 * using a MutationObserver, and hides LastPass's fill menu while an opted-out field has focus.
 *
 * CSS selectors are not reliable for the icons because:
 * - LastPass has changed its injected element selectors across versions.
 * - Injected iframes cannot be styled by the parent page's CSS.
 * - Some elements are added asynchronously after React renders.
 *
 * This hook watches the document for new elements matching known icon
 * patterns and removes them immediately. It runs once at app mount (called
 * from App.tsx) and disconnects on unmount.
 *
 * Authentication happens on Azure Entra CIAM's domain, so on this origin the
 * icons only interfere with address autocomplete.
 *
 * Business rule (PBI #1332): LastPass's root container
 * (`div[data-lastpass-root]`) must never be removed. LastPass injects it once
 * at page load, keeps a JavaScript reference to it, and renders its WebAuthn
 * "Save passkey" dialog into that same node when `navigator.credentials.create`
 * runs. An earlier version of this hook deleted the container, so the dialog
 * rendered into a detached node, nothing was visible, LastPass never answered,
 * and passkey registration hung until the watchdog fired. Only per-field icon
 * elements are listed below. See `.cursor/rules/password-manager-dom.mdc` and
 * `infrastructure/PASSKEYS.md`.
 *
 * Business rule (birthday / contact form menu): LastPass draws its identity fill menu in a closed
 * shadow root on that same container, so the page cannot empty it. It also ignores
 * `data-lpignore` and `autocomplete="off"` for users who have its "Respect AutoComplete=off"
 * preference disabled (the default for many). To give every user the same experience, the
 * container is hidden, never removed, only while a `data-lpignore="true"` field has focus, and
 * never while a passkey ceremony runs.
 */

/**
 * CSS selectors matching known LastPass / 1Password in-field icon elements.
 * Exported so `usePasswordManagerSuppressor.test.ts` can prove none of them
 * matches a protected container (see {@link PASSWORD_MANAGER_PROTECTED_SELECTORS}).
 */
export const PASSWORD_MANAGER_ICON_SELECTORS = [
  // LastPass — current versions (icon overlay, not the shared root container)
  'div[data-lastpass-icon-root]',
  // LastPass — older versions
  'div[id^="__lpform_"]',
  'span[data-lp-icon="true"]',
  // LastPass icon injected as child of input wrapper
  '[data-lastpass-infield]',
  // 1Password
  '[data-1p-ignore-input-wrapper]',
  'com-1password-button',
] as const;

/**
 * Password-manager containers that host passkey / WebAuthn UI and therefore
 * must never be removed by this hook. Add to this list, never to the icon
 * list, when a new manager's dialog host is identified.
 */
export const PASSWORD_MANAGER_PROTECTED_SELECTORS = [
  // LastPass shared root: injected at load, hosts the Save/Use passkey dialog.
  'div[data-lastpass-root]',
] as const;

/**
 * Class set on `<html>` while an opted-out field has focus. `style.css` hides the LastPass host
 * under this class as a first line of defence; {@link applyHostHiding} adds inline `!important`
 * values because LastPass sets `display: initial !important` inline on the host, which a
 * stylesheet rule cannot beat.
 */
export const PASSWORD_MANAGER_FIELD_FOCUS_CLASS = 'ss-suppress-password-manager';

/** Selector for the LastPass host that is hidden (never removed) while an opted-out field has focus. */
const LASTPASS_HOST_SELECTOR = PASSWORD_MANAGER_PROTECTED_SELECTORS[0];

/** Attribute our forms put on inputs where password-manager menus are unwanted (#661). */
const OPT_OUT_FIELD_SELECTOR = '[data-lpignore="true"]';

/**
 * Spread onto Mantine inputs (and searchable Select comboboxes) where password-manager
 * fill menus are unwanted. Focus on these fields activates {@link PASSWORD_MANAGER_FIELD_FOCUS_CLASS}.
 */
export const PASSWORD_MANAGER_OPT_OUT_FIELD_PROPS = {
  'data-lpignore': 'true',
  autoComplete: 'off',
} as const;

/**
 * Inline properties set on the LastPass host while hiding. `opacity` and `pointer-events` are
 * included because content inside the host's shadow root cannot override them.
 */
const HOST_HIDING_PROPERTIES = [
  ['display', 'none'],
  ['visibility', 'hidden'],
  ['opacity', '0'],
  ['pointer-events', 'none'],
] as const;

type SavedInlineStyles = Record<string, { value: string; priority: string }>;

/** Inline values LastPass had on the host before we hid it, so release restores them exactly. */
const savedHostInlineStyles = new WeakMap<HTMLElement, SavedInlineStyles>();

/**
 * Number of callers that currently need password-manager UI to stay on the
 * page. Reference-counted so overlapping ceremonies cannot resume removal
 * while one of them is still waiting on the extension.
 */
let suppressionSuspensions = 0;

/** True while any caller has asked for password-manager UI to be left alone. */
export function isPasswordManagerSuppressionSuspended(): boolean {
  return suppressionSuspensions > 0;
}

/** True while the `<html>` class asks for the LastPass host to stay hidden. */
function isFieldFocusHidingActive(): boolean {
  return document.documentElement.classList.contains(PASSWORD_MANAGER_FIELD_FOCUS_CLASS);
}

/**
 * Hides (or restores) every LastPass host with inline `!important` styles, remembering LastPass's
 * own inline values so the passkey dialog sees the host exactly as LastPass left it.
 */
function applyHostHiding(hide: boolean): void {
  document.querySelectorAll<HTMLElement>(LASTPASS_HOST_SELECTOR).forEach((host) => {
    if (hide) {
      if (!savedHostInlineStyles.has(host)) {
        const saved: SavedInlineStyles = {};
        for (const [property] of HOST_HIDING_PROPERTIES) {
          saved[property] = {
            value: host.style.getPropertyValue(property),
            priority: host.style.getPropertyPriority(property),
          };
        }
        savedHostInlineStyles.set(host, saved);
      }
      for (const [property, value] of HOST_HIDING_PROPERTIES) {
        host.style.setProperty(property, value, 'important');
      }
      return;
    }

    const saved = savedHostInlineStyles.get(host);
    if (!saved) {
      return;
    }
    for (const [property] of HOST_HIDING_PROPERTIES) {
      const previous = saved[property];
      if (previous.value) {
        host.style.setProperty(property, previous.value, previous.priority);
      } else {
        host.style.removeProperty(property);
      }
    }
    savedHostInlineStyles.delete(host);
  });
}

/** Turns host hiding on or off. Never turns it on while a passkey ceremony is suspended. */
function setFieldFocusHiding(active: boolean): void {
  const enabled = active && !isPasswordManagerSuppressionSuspended();
  document.documentElement.classList.toggle(PASSWORD_MANAGER_FIELD_FOCUS_CLASS, enabled);
  applyHostHiding(enabled);
}

function isOptOutField(target: EventTarget | null): boolean {
  return target instanceof Element && target.matches(OPT_OUT_FIELD_SELECTOR);
}

/**
 * Pauses removal of password-manager elements until the returned function is
 * called. Used around `navigator.credentials.create` / `.get` as a safety net:
 * if a password manager ever draws part of its passkey UI with an element that
 * matches an icon selector, the ceremony still gets to show it. It also shows the
 * LastPass host for the whole ceremony. The release function is idempotent.
 */
export function suspendPasswordManagerSuppression(): () => void {
  suppressionSuspensions += 1;

  if (typeof document !== 'undefined') {
    setFieldFocusHiding(false);
  }
  let released = false;

  return () => {
    if (released) {
      return;
    }
    released = true;
    suppressionSuspensions -= 1;

    // Clear anything the extension left behind once the last ceremony ends.
    if (suppressionSuspensions === 0 && typeof document !== 'undefined') {
      removePasswordManagerElements();
    }
  };
}

function removePasswordManagerElements(): void {
  if (isPasswordManagerSuppressionSuspended()) {
    return;
  }

  for (const selector of PASSWORD_MANAGER_ICON_SELECTORS) {
    try {
      document.querySelectorAll(selector).forEach((el) => el.remove());
    } catch {
      // Ignore invalid selectors (e.g. custom elements not supported)
    }
  }
}

/**
 * Installs a document-level MutationObserver that removes password-manager
 * in-field icon elements whenever they are added to the DOM, plus focus listeners that hide
 * the LastPass fill menu on opted-out fields.
 *
 * Call once at app root level (App.tsx).
 */
export function usePasswordManagerSuppressor(): void {
  useEffect(() => {
    // Remove any elements already present on mount
    removePasswordManagerElements();

    const observer = new MutationObserver((mutations) => {
      let needsScan = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          needsScan = true;
          break;
        }
      }

      if (needsScan) {
        removePasswordManagerElements();

        // LastPass may create its host after focus landed in the field.
        if (isFieldFocusHidingActive()) {
          applyHostHiding(true);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const handleFocusIn = (event: FocusEvent) => {
      if (isOptOutField(event.target)) {
        setFieldFocusHiding(true);
      }
    };
    const handleFocusOut = (event: FocusEvent) => {
      if (isOptOutField(event.target)) {
        setFieldFocusHiding(false);
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      setFieldFocusHiding(false);
    };
  }, []);
}
